import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarPlus,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import {
  useConnectCalendar,
  useDisconnectCalendar,
  useSyncCalendar,
  useUserCalendars,
} from '../../hooks/useUserCalendars';
import type { CalendarConnection } from '../../lib/userCalendarsApi';

/**
 * Per-user Google Calendar integration (CLE-236).
 *
 * Lists a user's connected Google Calendars with status, and lets the user
 * connect a new one (OAuth), disconnect, or force a sync. An ADMIN viewing
 * another user sees the same status list (read-only) but cannot connect on
 * their behalf — the OAuth grant must come from that user's own Google login.
 *
 * `userId` is the User id (Person.userId); when null the person has no user
 * account and the section is hidden by the caller.
 */
export function CalendarIntegration({ userId }: { userId: string }) {
  const currentUser = useAuthStore((s) => s.user);
  const isSelf = currentUser?.id === userId;
  const isAdmin = currentUser?.role === 'ADMIN';

  const { data, isLoading, isError } = useUserCalendars(userId);
  const connect = useConnectCalendar(userId);
  const disconnect = useDisconnectCalendar(userId);
  const sync = useSyncCalendar(userId);

  const [searchParams] = useSearchParams();
  const calendarResult = searchParams.get('calendar');
  const calendarReason = searchParams.get('reason');

  const [label, setLabel] = useState('');

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Se încarcă conexiunile de calendar…
      </p>
    );
  }
  if (isError || !data) {
    return (
      <p className="text-sm text-red-600">Conexiunile de calendar nu au putut fi încărcate.</p>
    );
  }

  const { connections, googleConfigured } = data;

  return (
    <div className="space-y-4">
      {/* OAuth return banner */}
      {calendarResult === 'connected' && (
        <p className="flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> Calendar conectat cu succes.
        </p>
      )}
      {calendarResult === 'error' && (
        <p className="flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4" /> Calendarul nu a putut fi conectat
          {calendarReason ? ` (${calendarReason})` : ''}.
        </p>
      )}

      {!googleConfigured && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Google OAuth nu este configurat. Un administrator trebuie să seteze Client ID și Client
          Secret în Setări → Integrări → Google Calendar; apoi fiecare membru își poate conecta
          propriul calendar de aici.
        </p>
      )}

      {connections.length === 0 ? (
        <p className="text-sm text-gray-500">Niciun calendar conectat.</p>
      ) : (
        <ul className="space-y-2">
          {connections.map((conn) => (
            <ConnectionRow
              key={conn.id}
              conn={conn}
              canManage={isSelf || isAdmin}
              canReauth={isSelf}
              onDisconnect={() => disconnect.mutate(conn.id)}
              onSync={() => sync.mutate(conn.id)}
              onReauth={() => connect.mutate(conn.calendarLabel ?? undefined)}
              disconnecting={disconnect.isPending && disconnect.variables === conn.id}
              syncing={sync.isPending && sync.variables === conn.id}
            />
          ))}
        </ul>
      )}

      {/* Connect new calendar — only the user themselves can grant access. */}
      {isSelf && googleConfigured && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Etichetă (ex. Serviciu, Personal)"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            type="button"
            onClick={() => connect.mutate(label.trim() || undefined)}
            disabled={connect.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {connect.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CalendarPlus className="h-4 w-4" />
            )}
            Conectează Google Calendar
          </button>
        </div>
      )}
      {!isSelf && isAdmin && (
        <p className="text-xs text-gray-400">
          Vizualizare ca administrator: statusul conexiunii este doar pentru citire. Doar
          utilizatorul își poate conecta propriul calendar.
        </p>
      )}
    </div>
  );
}

function ConnectionRow({
  conn,
  canManage,
  canReauth,
  onDisconnect,
  onSync,
  onReauth,
  disconnecting,
  syncing,
}: {
  conn: CalendarConnection;
  canManage: boolean;
  canReauth: boolean;
  onDisconnect: () => void;
  onSync: () => void;
  onReauth: () => void;
  disconnecting: boolean;
  syncing: boolean;
}) {
  const expired = conn.status === 'expired';
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">{conn.calendarEmail}</span>
          {conn.calendarLabel && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {conn.calendarLabel}
            </span>
          )}
          {expired ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" /> Expirat
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              <CheckCircle2 className="h-3 w-3" /> Conectat
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-gray-400">
          Ultima sincronizare:{' '}
          {conn.lastSyncAt ? new Date(conn.lastSyncAt).toLocaleString('ro-RO') : 'niciodată'}
        </p>
      </div>

      {canManage && (
        <div className="flex items-center gap-1.5">
          {expired && canReauth && (
            <button
              type="button"
              onClick={onReauth}
              className="rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50"
            >
              Reautentifică
            </button>
          )}
          <button
            type="button"
            onClick={onSync}
            disabled={syncing}
            title="Sincronizează acum"
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {syncing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Sincronizează
          </button>
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnecting}
            title="Deconectează"
            className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
          >
            {disconnecting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Deconectează
          </button>
        </div>
      )}
    </li>
  );
}
